import { useState, useEffect } from 'react';
import api from '../api/client';
import { STATUS_LABEL, STATUS_COLOR, won, amountDelta, dateOf } from '../utils/versionUtils';

const ST = {
  added:   { label:'추가', bg:'#f0fdf4', fg:'#16a34a' },
  removed: { label:'삭제', bg:'#fef2f2', fg:'#dc2626' },
  changed: { label:'변경', bg:'#fffbeb', fg:'#b45309' },
  same:    { label:'동일', bg:'transparent', fg:'#94a3b8' },
};

// 두 버전 비교 모달. aId/bId 는 어느 쪽이 먼저든 상관없음 — 버전 번호가 작은 쪽을 왼쪽(이전)으로 표시
export default function VersionCompareModal({ aId, bId, onClose }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [onlyChanged, setOnlyChanged] = useState(true);

  useEffect(() => {
    let alive = true;
    api.get(`/estimates/compare?a=${aId}&b=${bId}`)
      .then(d => {
        if (!alive) return;
        // 이전 버전이 a, 이후 버전이 b 가 되도록 정렬
        if (d.a.version > d.b.version) {
          const flip = { ...d, a: d.b, b: d.a, summary: { ...d.summary, added: d.summary.removed, removed: d.summary.added, total_diff: -d.summary.total_diff, profit_diff: -d.summary.profit_diff },
            fields: d.fields.map(f => ({ ...f, a: f.b, b: f.a })),
            items: d.items.map(r => ({ ...r, a: r.b, b: r.a, status: r.status === 'added' ? 'removed' : r.status === 'removed' ? 'added' : r.status, name: (r.b || r.a)?.name || r.name })) };
          setData(flip);
        } else setData(d);
      })
      .catch(e => alive && setError(e.message));
    return () => { alive = false; };
  }, [aId, bId]);

  const head = (v, label) => (
    <div style={{ flex:1, border:'1px solid #e2e8f0', borderRadius:8, padding:'10px 12px', background:'#fff', minWidth:0 }}>
      <div style={{ display:'flex', alignItems:'center', gap:6, marginBottom:4 }}>
        <span style={{ fontWeight:800, fontSize:15 }}>v{v.version}</span>
        <span style={{ fontSize:10, color:'#94a3b8' }}>{label}</span>
        <span style={{ marginLeft:'auto', fontSize:11, fontWeight:600, color:STATUS_COLOR[v.status] }}>● {STATUS_LABEL[v.status] || v.status}</span>
      </div>
      <div style={{ fontSize:11, color:'#64748b' }}>{dateOf(v)} · {v.author_name || '-'}</div>
      <div style={{ fontSize:15, fontWeight:700, marginTop:2 }}>{won(v.total_amount)}</div>
      {v.margin && <div style={{ fontSize:11, color:'#475569', marginTop:2 }}>공헌이익 <b>{won(v.margin.profit)}</b> ({v.margin.rate.toFixed(1)}%)</div>}
      {v.version_note && <div style={{ fontSize:11, color:'#475569', marginTop:4, fontStyle:'italic' }}>“{v.version_note}”</div>}
    </div>
  );

  const cell = (r, f, fmt = (x) => x) => {
    const av = r.a ? r.a[f] : null, bv = r.b ? r.b[f] : null;
    const changed = r.status === 'changed' && r.changes.includes(f);
    if (!r.a) return <span style={{ color:'#16a34a' }}>{bv == null ? '-' : fmt(bv)}</span>;
    if (!r.b) return <span style={{ color:'#dc2626', textDecoration:'line-through' }}>{av == null ? '-' : fmt(av)}</span>;
    if (!changed) return <span style={{ color:'#64748b' }}>{bv == null ? '-' : fmt(bv)}</span>;
    return <span><span style={{ color:'#94a3b8', textDecoration:'line-through' }}>{av == null ? '-' : fmt(av)}</span> <b style={{ color:'#b45309' }}>→ {bv == null ? '-' : fmt(bv)}</b></span>;
  };

  const d = data;
  const delta = d && amountDelta(d.b.total_amount, d.a.total_amount);
  const pDelta = d && d.a.margin && d.b.margin && amountDelta(d.b.margin.profit, d.a.margin.profit);
  const rows = d ? d.items.filter(r => !onlyChanged || r.status !== 'same') : [];

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ width:860, maxWidth:'94vw' }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>버전 비교{d ? ` — v${d.a.version} → v${d.b.version}` : ''}</h3>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body" style={{ maxHeight:'72vh', overflowY:'auto' }}>
          {error && <div style={{ color:'#dc2626' }}>{error}</div>}
          {!d && !error && <div style={{ padding:30, textAlign:'center', color:'#94a3b8' }}>불러오는 중...</div>}
          {d && (<>
            <div style={{ display:'flex', gap:10, alignItems:'stretch', marginBottom:10 }}>
              {head(d.a, '이전')}
              <div style={{ display:'flex', alignItems:'center', color:'#94a3b8' }}>→</div>
              {head(d.b, '이후')}
            </div>
            <div style={{ display:'flex', gap:6, alignItems:'center', flexWrap:'wrap', marginBottom:12, fontSize:12 }}>
              {['added','removed','changed','same'].map(k => (
                <span key={k} style={{ padding:'2px 9px', borderRadius:10, fontWeight:600, background:ST[k].bg === 'transparent' ? '#f1f5f9' : ST[k].bg, color:ST[k].fg }}>
                  {ST[k].label} {d.summary[k]}
                </span>
              ))}
              <span style={{ marginLeft:'auto', fontWeight:700, color:delta ? delta.color : '#64748b' }}>
                합계 {delta ? delta.text : '변동 없음'}
                {pDelta && <span style={{ color:pDelta.color, marginLeft:10 }}>공헌이익 {pDelta.text}</span>}
              </span>
            </div>

            {d.fields.length > 0 && (
              <div style={{ marginBottom:12, border:'1px solid #e2e8f0', borderRadius:8, overflow:'hidden' }}>
                <div style={{ padding:'6px 10px', background:'#f8fafc', fontSize:11, fontWeight:700, color:'#64748b' }}>기본 정보 변경</div>
                {d.fields.map(f => (
                  <div key={f.key} style={{ display:'flex', gap:10, padding:'6px 10px', fontSize:12, borderTop:'1px solid #f1f5f9' }}>
                    <span style={{ width:90, color:'#64748b', flexShrink:0 }}>{f.label}</span>
                    <span style={{ color:'#94a3b8', textDecoration:'line-through', whiteSpace:'pre-wrap', flex:1 }}>{f.a || '(없음)'}</span>
                    <span style={{ color:'#b45309', fontWeight:600, whiteSpace:'pre-wrap', flex:1 }}>→ {f.b || '(없음)'}</span>
                  </div>
                ))}
              </div>
            )}

            <div style={{ display:'flex', alignItems:'center', marginBottom:6 }}>
              <span style={{ fontSize:12, fontWeight:700, color:'#475569' }}>항목 비교</span>
              <label style={{ marginLeft:'auto', fontSize:12, color:'#64748b', cursor:'pointer', display:'flex', alignItems:'center', gap:5 }}>
                <input type="checkbox" checked={onlyChanged} onChange={e => setOnlyChanged(e.target.checked)} /> 변경된 항목만 보기
              </label>
            </div>
            <table style={{ width:'100%', fontSize:12 }}>
              <thead><tr><th style={{ width:56 }}>구분</th><th style={{ textAlign:'left' }}>항목</th><th style={{ textAlign:'right' }}>수량</th><th style={{ textAlign:'right' }}>공급가액</th><th style={{ textAlign:'right' }}>공급금액</th></tr></thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={i} style={{ background:ST[r.status].bg }}>
                    <td style={{ textAlign:'center', fontWeight:700, color:ST[r.status].fg }}>{ST[r.status].label}</td>
                    <td style={{ whiteSpace:'pre-wrap', maxWidth:280 }}>{(r.b || r.a).name}</td>
                    <td style={{ textAlign:'right' }}>{cell(r, 'quantity')}</td>
                    <td style={{ textAlign:'right' }}>{cell(r, 'unit_price', won)}</td>
                    <td style={{ textAlign:'right', fontWeight:600 }}>{cell(r, 'line_total', won)}</td>
                  </tr>
                ))}
                {rows.length === 0 && <tr><td colSpan={5} style={{ textAlign:'center', padding:20, color:'#94a3b8' }}>{onlyChanged ? '변경된 항목이 없습니다.' : '항목이 없습니다.'}</td></tr>}
              </tbody>
            </table>
          </>)}
        </div>
      </div>
    </div>
  );
}
